// src/shared/GlobalContext.js

import React, { createContext, useState, useEffect, useContext, useRef } from "react";
import LoaderQlikResources from "./components/LoaderQlikResources";
import stripQueryParamsFromUrl from "./utils/stripQueryParamsFromUrl";

const GlobalContext = createContext({ qlik: null, app: null });

/**
 * GlobalProvider bootstraps Qlik via the global window.require (RequireJS).
 * It avoids webpack's bundling by referencing window.require explicitly.
 */
function GlobalProvider({ children }) {
  // 1) Read environment variables
  const QLIK_SERVER = process.env.REACT_APP_QLIK_SERVER;
  const QLIK_LINK_ADDRESS = process.env.REACT_APP_QLIK_LINK_ADDRESS;
  const QLIK_SCHEME = process.env.REACT_APP_QLIK_SERVER_SCHEME;
  const QLIK_PORT = process.env.REACT_APP_QLIK_SERVER_PORT;
  const QLIK_PROXY = process.env.REACT_APP_QLIK_PROXY;

  // Site-specific
  const QLIK_APP_ID = process.env.REACT_APP_QLIK_APP;
  const QLIK_THEME = process.env.REACT_APP_QLIK_THEME;
  const QLIK_DEFAULT_BOOKMARK = process.env.REACT_APP_DEFAULT_BOOKMARK;

  const [qlik, setQlik] = useState(null);
  const [app, setApp] = useState(null);
  const [error, setError] = useState(null);

  // Build RequireJS URL
  const rawProxy = (QLIK_PROXY || "").trim();
  const cleanProxy = rawProxy.replace(/^\/+|\/+$/g, "");
  const prefix = cleanProxy ? `/${cleanProxy}` : "";
  const isSecure = QLIK_SCHEME === "https";
  const hostAndPort = QLIK_PORT ? `${QLIK_SERVER}:${QLIK_PORT}` : QLIK_SERVER;
  const requireBaseUrl = `${QLIK_SCHEME}://${hostAndPort}${prefix}/resources`;

  useEffect(() => {
    // Append the RequireJS script
    const ticket = new URLSearchParams(window.location.search).get("qlikTicket");
    const script = document.createElement("script");
    script.id = "qlik-requirejs-tag";
    script.src = `${requireBaseUrl}/assets/external/requirejs/require.js${
      ticket ? `?qlikTicket=${encodeURIComponent(ticket)}` : ""
    }`;

    script.onload = () => {
      const req = window.require; // use global require from RequireJS
      if (!req) {
        setError("RequireJS not available on window");
        return;
      }
      req.config({
        baseUrl: requireBaseUrl,
        paths: { "js/qlik": "js/qlik" },
        config: { qlik: {} }
      });
      // Load the Qlik engine
      req(["js/qlik"], (qlikAPI) => {
        console.log("▶ Qlik API loaded");
        if (QLIK_THEME && qlikAPI.theme?.apply) {
          try { qlikAPI.theme.apply(QLIK_THEME); } catch (e) { console.warn("Theme apply error", e); }
        }
        const qConfig = { host: QLIK_SERVER, prefix: `${prefix}/`, port: QLIK_PORT, isSecure };
        const openedApp = qlikAPI.openApp(QLIK_APP_ID, qConfig);
        if (!openedApp) {
          setError("Failed to open Qlik app");
          return;
        }
        if (QLIK_DEFAULT_BOOKMARK && openedApp.bookmark?.apply) {
          try { openedApp.bookmark.apply(QLIK_DEFAULT_BOOKMARK); } catch (e) { console.warn("Bookmark apply error", e); }
        }
        setQlik(qlikAPI);
        setApp(openedApp);
      }, () => setError("Could not load Qlik API module."));
    };

    script.onerror = () => setError("Failed to load Qlik require.js script.");
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById("qlik-requirejs-tag");
      if (existing) existing.remove();
    };
  }, [QLIK_SERVER, QLIK_SCHEME, QLIK_PORT, QLIK_PROXY, QLIK_APP_ID, QLIK_THEME, QLIK_DEFAULT_BOOKMARK]);

  const didStripQlikTicket = useRef(false); // NEW
  useEffect(() => {
    if (!app || didStripQlikTicket.current) return;
    // At this point, SESSION_CREATED/ATTACHED has succeeded and openApp() returned.
    stripQueryParamsFromUrl(["qlikTicket"]);
    didStripQlikTicket.current = true;
  }, [app]);

  if (error) {
    return <div style={{ padding: 20, color: "red" }}>{error}</div>;
  }
  if (!app || !qlik) {
    return <LoaderQlikResources />;
  }

  return (
    <GlobalContext.Provider
      value={{
        qlik,
        app,
        qlikLinkRoot: `${QLIK_SCHEME}://${QLIK_LINK_ADDRESS}`
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
}

function useGlobalContext() {
  return useContext(GlobalContext);
}

export default GlobalContext;
export { GlobalProvider, useGlobalContext };
